import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Boxes, Check, CheckCircle2, CreditCard, Eye, Globe, ImagePlus,
    Loader2, Rocket, Store, X,
} from "lucide-react";
import { BO, Btn, Toggle, inputCls } from "@/components/account/Shell";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";

/** A labelled field with the eye that opens a plain-English explanation under it. */
function Field({ name, label, help, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="mb-3" data-testid={`wizard-field-${name}`}>
            <div className="mb-1 flex items-center gap-1.5">
                <label className="text-[13px] font-semibold text-[#1a1a1a]">{label}</label>
                {help && (
                    <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`wizard-eye-${name}`}
                        aria-label={`What is ${label}?`}
                        className={`grid h-[18px] w-[18px] place-items-center border ${open
                            ? "border-[#1a56db] bg-[#eff4ff] text-[#1a56db]"
                            : "border-[#d0d0d0] bg-white text-[#4a4a4a] hover:border-[#1a1a1a] hover:text-[#1a1a1a]"}`}>
                        <Eye size={11} />
                    </button>
                )}
            </div>
            {children}
            {open && help && (
                <p className="mt-1.5 border-l-2 border-[#1a56db] bg-[#eff4ff] px-2 py-1.5 text-[13px] leading-snug text-[#1a1a1a]"
                    data-testid={`wizard-help-${name}`}>{help}</p>
            )}
        </div>
    );
}

const Row = ({ done, children, testid }) => (
    <li className="flex items-start gap-2 text-[13px] text-[#1a1a1a]" data-testid={testid}>
        <span className={`mt-[3px] grid h-3.5 w-3.5 shrink-0 place-items-center border ${done
            ? "border-[#067647] bg-[#067647] text-white" : "border-[#d0d0d0] bg-white"}`}>
            {done && <Check size={9} />}
        </span>
        {children}
    </li>
);

/**
 * The setup window (ADD-ON 28). It floats over whatever back-office screen you were on —
 * nine questions on the right, the checklist on the left, and one magic button at the top
 * that opens the whole store in a single click.
 */
export default function WizardModal({ onClose, onSaved }) {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState(null);
    const [form, setForm] = useState({});
    const fileRef = useRef(null);

    const load = useCallback(() => {
        api.get("/store/wizard").then(({ data }) => {
            setD(data);
            setForm((f) => ({ ...data.answers, ...f }));
            setStep((s) => s || data.step);
        }).catch((e) => setD({ error: formatApiError(e.response?.data?.detail) }));
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const esc = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", esc);
        return () => document.removeEventListener("keydown", esc);
    }, [onClose]);

    const apply = (res) => {
        setD(res);
        setForm((f) => ({ ...res.answers, ...f }));
        if (res.note) toast.success(res.note);
        onSaved?.();
    };

    const run = async (fn) => {
        setBusy(true);
        try { const { data: res } = await fn(); apply(res); return res; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const shell = (body) => (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 sm:p-6"
            data-testid="wizard-modal">
            <div className="fixed inset-0 bg-black/55" onClick={onClose} />
            <div className="relative w-full max-w-5xl border border-[#d0d0d0] bg-white shadow-2xl">{body}</div>
        </div>
    );

    if (!d) return shell(
        <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>
    );

    if (d.error) return shell(
        <div className="p-8 text-center" data-testid="wizard-locked">
            <Store size={26} className="mx-auto text-[#4a4a4a]" />
            <h2 className="mt-3 text-[19px] font-bold text-[#1a1a1a]">The wizard sets up a store.</h2>
            <p className="mt-1.5 text-sm text-[#4a4a4a]">{d.error}</p>
            <div className="mt-5 flex justify-center gap-2">
                <Link to="/account/sell"><Btn tone="solid" testid="wizard-open-space">See the spaces</Btn></Link>
                <Btn onClick={onClose} testid="wizard-close-locked">Close</Btn>
            </div>
        </div>
    );

    const steps = d.steps;
    const idx = Math.max(steps.findIndex((s) => s.key === step), 0);
    const cur = steps[idx];
    const pct = Math.round((d.progress.done / d.progress.total) * 100);
    const help = (k) => d.field_help?.[k] || "";
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const saveStep = (patch, go = 1) => {
        const next = steps[Math.min(Math.max(idx + go, 0), steps.length - 1)];
        return run(() => api.put("/store/wizard/step", { step: cur.key, next_step: next.key, ...patch }))
            .then((res) => { if (res) setStep(next.key); return res; });
    };

    const patchFor = {
        business: () => ({ name: form.name, phone: form.phone }),
        logo: () => ({ logo: form.logo }),
        address: () => ({ address: form.address }),
        pickup: () => ({ hours: form.hours, shipping: form.shipping || undefined }),
        domain: () => ({ subdomain: form.subdomain ?? "", custom_domain: form.custom_domain ?? "" }),
    };

    const pickLogo = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const rec = await uploadArtwork(file);
            setForm((f) => ({ ...f, logo: rec.url }));
            await api.put("/store/wizard/step", { step: "logo", next_step: "logo", logo: rec.url });
            toast.success("Logo saved");
            load();
            onSaved?.();
        } catch { toast.error("Upload failed"); }
        finally { setBusy(false); }
    };

    const text = (k, label, placeholder, type = "text") => (
        <Field name={k} label={label} help={help(k)}>
            <input type={type} className={inputCls} placeholder={placeholder} value={form[k] ?? ""}
                onChange={set(k)} data-testid={`wizard-input-${k}`} />
        </Field>
    );

    const BODY = {
        business: (
            <>
                {text("name", "Business name", "Benny's Graphics")}
                {text("phone", "Phone", "(347) 573-3875", "tel")}
            </>
        ),
        logo: (
            <Field name="logo" label="Your logo" help={help("logo")}>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="grid h-20 w-20 place-items-center overflow-hidden border border-[#d0d0d0] bg-[#f5f5f5]">
                        {form.logo ? <img src={mediaUrl(form.logo)} alt="" className="h-full w-full object-contain" />
                            : <Store size={20} className="text-[#4a4a4a]" />}
                    </div>
                    <Btn onClick={() => fileRef.current?.click()} disabled={busy} testid="wizard-logo-upload" tone="solid">
                        {busy ? <Loader2 size={11} className="animate-spin" /> : <ImagePlus size={11} />} Upload a logo
                    </Btn>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                        onChange={(e) => pickLogo(e.target.files?.[0])} />
                    <span className="text-[13px] text-[#4a4a4a]">Skip it and we set your name in type.</span>
                </div>
            </Field>
        ),
        address: text("address", "Pickup address", "1309 78th Street, Brooklyn, NY"),
        pickup: (
            <>
                {text("hours", "Pickup hours", "Mon–Fri 9–6, Sat 10–4")}
                <Field name="shipping" label="Shipping promise" help={help("shipping")}>
                    <div className="flex flex-wrap gap-1.5" data-testid="wizard-shipping">
                        {(d.shipping_modes || []).map((m) => (
                            <button key={m.key} onClick={() => setForm((f) => ({ ...f, shipping: m.key }))}
                                data-testid={`wizard-shipping-${m.key}`}
                                className={`border px-2.5 py-1 text-[13px] font-semibold ${form.shipping === m.key
                                    ? "border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv"
                                    : "border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]"}`}>
                                {m.label}
                            </button>
                        ))}
                    </div>
                </Field>
            </>
        ),
        payments: (
            <Field name="stripe" label="Your register" help={help("stripe")}>
                {d.stripe?.charges_enabled ? (
                    <p className="flex items-center gap-2 text-sm text-[#067647]" data-testid="wizard-stripe-ok">
                        <CheckCircle2 size={15} /> Stripe is connected. Card payments go into your own account.
                    </p>
                ) : (
                    <div className="flex flex-wrap items-center gap-3">
                        <p className="min-w-[220px] flex-1 text-sm text-[#1a1a1a]">
                            Connect Stripe and your shop can take cards today. You can also open the doors first
                            and take money at the counter.
                        </p>
                        <Link to="/account/payouts" target="_blank" rel="noreferrer">
                            <Btn tone="solid" testid="wizard-stripe-connect"><CreditCard size={11} /> Connect Stripe</Btn>
                        </Link>
                    </div>
                )}
            </Field>
        ),
        consignment: (
            <Field name="consignment" label="Let 13th Ave print it" help={help("consignment")}>
                {d.consignment_eligible ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <Toggle on={!!d.consignment?.enabled} disabled={busy} testid="wizard-consignment"
                            onChange={(v) => run(() => api.put("/store/wizard/step",
                                { step: "consignment", next_step: "consignment", consignment: v }))} />
                        <span className="text-sm text-[#1a1a1a]">
                            You keep {Math.round((d.consignment?.rate || 0.2) * 100)}% of every consigned sale.
                        </span>
                        <Link to="/account/consignment" className="text-[13px] font-semibold text-[#1a56db] hover:underline">
                            full consignment settings →
                        </Link>
                    </div>
                ) : (
                    <p className="text-sm text-[#4a4a4a]" data-testid="wizard-consignment-na">
                        Consignment Mode comes with the Pro Shop and Virtual Brick & Mortar plans. There is nothing to answer on this plan.
                    </p>
                )}
            </Field>
        ),
        products: (
            <>
                <Field name="catalog" label="Stock your shelves" help={help("catalog")}>
                    <div className="flex flex-wrap items-center gap-3">
                        <Btn tone="solid" disabled={busy || !d.can_catalog} testid="wizard-catalog-load"
                            onClick={() => run(() => api.post("/store/wizard/catalog"))}>
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <Boxes size={11} />}
                            Copy the house catalog in
                        </Btn>
                        <span className="text-sm text-[#1a1a1a]" data-testid="wizard-product-count">
                            {d.products} product{d.products === 1 ? "" : "s"} live
                        </span>
                        {!d.can_catalog && (
                            <span className="text-[13px] text-[#4a4a4a]">
                                A {d.tier_label} plan sells classified ads and classes. The product catalog comes with the Storefront plan.
                            </span>
                        )}
                    </div>
                </Field>
                <Field name="lines" label="Which counters are open" help={help("lines")}>
                    <div className="grid gap-1 sm:grid-cols-2" data-testid="wizard-rooms">
                        {d.rooms.map((r) => (
                            <label key={r.key} className="flex items-center gap-2 border border-[#d0d0d0] px-2 py-1.5 text-[13px] text-[#1a1a1a]">
                                <input type="checkbox" className="h-3.5 w-3.5 accent-[#1a1a1a]"
                                    data-testid={`wizard-room-${r.key}`} checked={d.lines?.[r.key] !== false}
                                    onChange={(e) => run(() => api.put("/store/wizard/step", {
                                        step: "products", next_step: "products", lines: { [r.key]: e.target.checked },
                                    }))} />
                                {r.label}
                            </label>
                        ))}
                    </div>
                </Field>
            </>
        ),
        domain: (
            <>
                <Field name="subdomain" label={`Your free address on ${d.mall_host}`} help={help("subdomain")}>
                    <div className="flex items-center">
                        <input className={inputCls} placeholder={d.suggested_subdomain}
                            value={form.subdomain ?? ""} onChange={set("subdomain")}
                            data-testid="wizard-input-subdomain" />
                        <span className="shrink-0 border border-l-0 border-[#d0d0d0] bg-[#f5f5f5] px-2 py-1.5 text-sm font-semibold text-[#4a4a4a]">
                            .{d.mall_host}
                        </span>
                    </div>
                </Field>
                {text("custom_domain", "Or your own domain", "shop.yourname.com")}
                <p className="flex items-start gap-2 text-[13px] text-[#4a4a4a]">
                    <Globe size={13} className="mt-0.5 shrink-0" />
                    Point an A record at our servers and we serve your site under your own name. We will email you
                    the exact record when you save it.
                </p>
            </>
        ),
        review: (
            <div>
                <ul className="space-y-1.5" data-testid="wizard-review">
                    {steps.filter((s) => s.key !== "review").map((s) => (
                        <Row key={s.key} done={d.done.includes(s.key)} testid={`wizard-review-${s.key}`}>
                            <span>
                                <span className="font-semibold">{s.label}</span>
                                {!d.done.includes(s.key) && (
                                    <button onClick={() => setStep(s.key)} data-testid={`wizard-review-go-${s.key}`}
                                        className="ml-2 text-[13px] font-semibold text-[#1a56db] hover:underline">
                                        answer it
                                    </button>
                                )}
                            </span>
                        </Row>
                    ))}
                </ul>
                <p className="mt-3 text-sm text-[#1a1a1a]">
                    Nothing above is required to open. {d.live
                        ? "Your shop is already open. Customers can check out right now."
                        : "Open your shop and it is listed in the directory."}
                </p>
                <div className="mt-3">
                    <Btn tone="solid" disabled={busy} testid="wizard-finish"
                        onClick={() => run(() => api.post("/store/wizard/finish"))}>
                        {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                        Open my store
                    </Btn>
                </div>
            </div>
        ),
    }[cur.key];

    return shell(
        <>
            <header className="border-b border-[#d0d0d0] bg-[#fafafa] px-3 py-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                            Setup checklist · {d.store?.name}
                        </p>
                        <h2 className="text-[17px] font-bold leading-tight text-[#1a1a1a]">Set up your store</h2>
                    </div>
                    <span className="ml-auto text-[13px] font-semibold tabular-nums text-[#1a1a1a]"
                        data-testid="wizard-progress">{d.progress.done}/{d.progress.total} done</span>
                    <button onClick={() => run(() => api.post("/store/wizard/go-live"))} disabled={busy}
                        data-testid="wizard-golive"
                        className="inline-flex items-center gap-1.5 border border-[#067647] bg-[#067647] px-2.5 py-1 text-[13px] font-bold text-white bo-inv hover:bg-[#04532f] disabled:opacity-50">
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Go live now
                    </button>
                    <button onClick={onClose} data-testid="wizard-modal-close" aria-label="Close setup"
                        className="grid h-7 w-7 place-items-center border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]">
                        <X size={14} />
                    </button>
                </div>
                <div className="mt-2 h-1.5 w-full bg-[#e4e4e4]" data-testid="wizard-progress-bar">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: BO.ink }} />
                </div>
            </header>

            <div className="grid gap-0 sm:grid-cols-[14rem_1fr]">
                <aside className="border-b border-[#d0d0d0] bg-[#fafafa] p-1.5 sm:border-b-0 sm:border-r">
                    <nav data-testid="wizard-sidebar">
                        {steps.map((s, i) => {
                            const done = d.done.includes(s.key);
                            const on = s.key === cur.key;
                            return (
                                <button key={s.key} onClick={() => setStep(s.key)} data-testid={`wizard-side-${s.key}`}
                                    className={`flex w-full items-center gap-2 border-l-2 px-2 py-[5px] text-left text-[13px] ${on
                                        ? "border-[#1a1a1a] bg-white font-semibold text-[#1a1a1a]"
                                        : "border-transparent text-[#1a1a1a] hover:bg-white"}`}>
                                    <span className={`grid h-4 w-4 shrink-0 place-items-center border text-[11px] font-bold tabular-nums ${done
                                        ? "border-[#067647] bg-[#067647] text-white"
                                        : "border-[#d0d0d0] bg-white text-[#4a4a4a]"}`}>
                                        {done ? <Check size={10} /> : i + 1}
                                    </span>
                                    <span className="truncate">{s.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                <div className="min-w-0 p-3">
                    <div className="flex items-start gap-2">
                        <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                                Step {idx + 1} of {steps.length} · {cur.label}
                            </p>
                            <h3 className="text-[17px] font-bold leading-tight text-[#1a1a1a]" data-testid="wizard-ask">
                                {cur.ask}
                            </h3>
                        </div>
                    </div>
                    {cur.why && (
                        <p className="mt-1.5 border-l-2 border-[#d0d0d0] pl-2 text-[13px] leading-snug text-[#4a4a4a]"
                            data-testid="wizard-why">{cur.why}</p>
                    )}

                    <div className="mt-3">{BODY}</div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#d0d0d0] pt-2.5">
                        <Btn disabled={busy || idx === 0} testid="wizard-back"
                            onClick={() => setStep(steps[Math.max(idx - 1, 0)].key)}>
                            <ArrowLeft size={11} /> Back
                        </Btn>
                        {idx < steps.length - 1 && (
                            <Btn tone="solid" disabled={busy} testid="wizard-next"
                                onClick={() => saveStep(patchFor[cur.key] ? patchFor[cur.key]() : {})}>
                                {busy ? <Loader2 size={11} className="animate-spin" /> : <ArrowRight size={11} />}
                                Save & next
                            </Btn>
                        )}
                        {idx < steps.length - 1 && (
                            <button onClick={() => setStep(steps[idx + 1].key)} data-testid="wizard-skip"
                                className="text-[13px] font-semibold text-[#4a4a4a] hover:text-[#1a1a1a] hover:underline">
                                skip this one
                            </button>
                        )}
                        <button onClick={onClose} data-testid="wizard-later"
                            className="ml-auto text-[13px] font-semibold text-[#4a4a4a] hover:text-[#1a1a1a] hover:underline">
                            finish later
                        </button>
                    </div>
                    {d.finished_at && (
                        <p className="mt-2 flex items-center gap-1.5 text-[13px] font-semibold text-[#067647]"
                            data-testid="wizard-finished">
                            <CheckCircle2 size={12} /> Setup is finished. Your shop is open.
                        </p>
                    )}
                </div>
            </div>
        </>
    );
}
